import { useEffect, useState } from "react";
import Button from "src/components/custom/Button";

const CERTIFICATE_URL =
  "https://dststg.s3.ap-south-1.amazonaws.com/certificate.png";
const GENERATE_DELAY_MS = 3500; // 3-4s fake loader

/**
 * CertificateButton
 *
 * Shows "Generate Certificate" -> loading spinner (3-4s) -> "View Certificate".
 * State is persisted per-trainee in localStorage (no DB write), so refreshing
 * the page after generating keeps showing "View Certificate" instead of
 * resetting back to "Generate Certificate".
 *
 * Props:
 * - registrationId: unique key to scope localStorage per trainee (required)
 * - className: optional extra classes to match surrounding buttons
 */
const CertificateButton = ({ registrationId, className = "" }) => {
  const storageKey = `certificate_generated_${registrationId}`;

  // idle | loading | generated
  const [status, setStatus] = useState("idle");

  // Restore state on mount / when registrationId becomes available
  useEffect(() => {
    if (!registrationId) return;
    const saved = localStorage.getItem(storageKey);
    if (saved === "true") {
      setStatus("generated");
    }
  }, [registrationId, storageKey]);

  const handleGenerate = () => {
    if (!registrationId) return;
    setStatus("loading");
    setTimeout(() => {
      setStatus("generated");
      try {
        localStorage.setItem(storageKey, "true");
      } catch (e) {
        // localStorage may be unavailable (private mode, quota, etc.)
        console.error("Could not persist certificate state:", e);
      }
    }, GENERATE_DELAY_MS);
  };

  const handleView = () => {
    window.open(CERTIFICATE_URL, "_blank", "noopener,noreferrer");
  };

  const title =
    status === "loading" ? (
       <div className="flex items-center gap-2 justify-center">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
        Generating Certificate.....
      </div>
    ) : status === "generated" ? (
        "View Certificate"
    ) : (
      "Generate Certificate"
    );

  return (
    <Button
      title={title}
      className={`py-2 px-8 !w-32 text-xs text-primary ${className}`}
      disabled={status === "loading"}
      onClick={status === "generated" ? handleView : handleGenerate}
      type="button"
    />
  );
};

export default CertificateButton;